<script setup lang="ts">
import { JsonForms } from '@jsonforms/vue'
import { vuetifyRenderers } from '@jsonforms/vue-vuetify'
import type { JsonFormsChangeEvent } from '@jsonforms/core'

definePage({
    meta: {
        id: 'dev.jsonforms',
        title: 'JSONForms Demo',
        icon: 'mdi-form-select',
        drawerIndex: 98,
        requiresAuth: false,
    },
})

// ── JSON Schema describing the form data ──────────────────────────────────────

const schema = {
    type: 'object',
    properties: {
        equipmentId: {
            type: 'string',
            title: "Identifiant de l'équipement",
        },
        equipmentType: {
            type: 'string',
            title: "Type d'équipement",
            enum: ['Tracteur', 'Moissonneuse-batteuse', 'Pulvérisateur', 'Semoir', 'Chargeur', 'Autre'],
        },
        date: {
            type: 'string',
            format: 'date',
            title: 'Date de l\'observation',
        },
        severity: {
            type: 'string',
            title: 'Niveau de sévérité',
            enum: ['Information', 'Avertissement', 'Critique'],
        },
        comment: {
            type: 'string',
            title: 'Commentaire',
            minLength: 5,
            maxLength: 500,
        },
        maintenanceRequired: {
            type: 'boolean',
            title: 'Maintenance requise',
        },
        estimatedCost: {
            type: 'number',
            title: 'Coût estimé (CAD)',
            minimum: 0,
        },
        technicianName: {
            type: 'string',
            title: 'Nom du technicien',
        },
    },
    required: ['equipmentId', 'equipmentType', 'date', 'severity', 'comment'],
}

// ── UI Schema describing the layout ──────────────────────────────────────────

const uischema = {
    type: 'VerticalLayout',
    elements: [
        {
            type: 'HorizontalLayout',
            elements: [
                {
                    type: 'Control',
                    scope: '#/properties/equipmentId',
                    options: { placeholder: 'ex: TRACT-001' },
                },
                {
                    type: 'Control',
                    scope: '#/properties/equipmentType',
                },
            ],
        },
        {
            type: 'HorizontalLayout',
            elements: [
                {
                    type: 'Control',
                    scope: '#/properties/date',
                },
                {
                    type: 'Control',
                    scope: '#/properties/severity',
                },
            ],
        },
        {
            type: 'Control',
            scope: '#/properties/comment',
            options: {
                multi: true,
            },
        },
        {
            type: 'HorizontalLayout',
            elements: [
                {
                    type: 'Control',
                    scope: '#/properties/maintenanceRequired',
                },
                {
                    type: 'Control',
                    scope: '#/properties/estimatedCost',
                    rule: {
                        effect: 'SHOW',
                        condition: {
                            scope: '#/properties/maintenanceRequired',
                            schema: { const: true },
                        },
                    },
                },
            ],
        },
        {
            type: 'Control',
            scope: '#/properties/technicianName',
        },
    ],
}

// ── State ─────────────────────────────────────────────────────────────────────

declare interface EquipmentComment {
    equipmentId?: string
    equipmentType?: string
    date?: string
    severity?: string
    comment?: string
    maintenanceRequired?: boolean
    estimatedCost?: number
    technicianName?: string
}

const renderers = [...vuetifyRenderers]

const formData = ref<EquipmentComment>({
    date: new Date().toISOString().split('T')[0],
    maintenanceRequired: false,
})
const formErrors = ref<unknown[]>([])
const isValid = computed(() => formErrors.value.length === 0)

const submittedEntries = ref<EquipmentComment[]>([])
const snackbarVisible = ref(false)
const snackbarMsg = ref('')

function onFormChange(event: JsonFormsChangeEvent) {
    formData.value = event.data as EquipmentComment
    formErrors.value = event.errors ?? []
}

function submitForm() {
    if (!isValid.value) return
    submittedEntries.value.unshift({ ...formData.value })
    snackbarMsg.value = 'Commentaire enregistré avec succès.'
    snackbarVisible.value = true
    // Reset form to defaults
    formData.value = {
        date: new Date().toISOString().split('T')[0],
        maintenanceRequired: false,
    }
}

function resetForm() {
    formData.value = {
        date: new Date().toISOString().split('T')[0],
        maintenanceRequired: false,
    }
    formErrors.value = []
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function severityColor(severity?: string) {
    if (severity === 'Critique') return 'error'
    if (severity === 'Avertissement') return 'warning'
    return 'info'
}

function severityIcon(severity?: string) {
    if (severity === 'Critique') return 'mdi-alert-circle'
    if (severity === 'Avertissement') return 'mdi-alert'
    return 'mdi-information'
}
</script>

<template>
    <v-card class="mx-4 my-4 page-card" rounded="xl">

        <!-- Page header -->
        <div class="page-header px-5 pt-5 pb-3">
            <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">
                <div class="d-flex align-center" style="gap: 12px;">
                    <v-avatar color="primary" variant="tonal" size="44" rounded="lg">
                        <v-icon icon="mdi-form-select" size="26" />
                    </v-avatar>
                    <div>
                        <div class="text-h6 font-weight-bold" style="line-height: 1.2;">JSONForms — Démo</div>
                        <div class="text-caption text-medium-emphasis">
                            Prototype d'intégration de JSONForms avec Vuetify 3
                        </div>
                    </div>
                </div>
                <div class="d-flex align-center" style="gap: 8px;">
                    <v-chip color="primary" variant="tonal" size="small" prepend-icon="mdi-tag">
                        @jsonforms/vue-vuetify 3.7
                    </v-chip>
                </div>
            </div>
        </div>

        <v-divider />

        <!-- Content -->
        <v-container fluid class="pa-4">
            <v-row>

                <!-- Left: Form -->
                <v-col cols="12" md="6">
                    <v-card variant="outlined" rounded="lg">
                        <v-card-title class="d-flex align-center" style="gap: 8px;">
                            <v-icon icon="mdi-pencil-box-outline" color="primary" />
                            Nouveau commentaire
                        </v-card-title>
                        <v-card-subtitle class="pb-2">
                            Formulaire piloté par JSON Schema + UI Schema
                        </v-card-subtitle>
                        <v-divider />
                        <v-card-text>
                            <json-forms
                                :data="formData"
                                :schema="schema"
                                :uischema="uischema"
                                :renderers="renderers"
                                validation-mode="ValidateAndShow"
                                @change="onFormChange"
                            />
                        </v-card-text>
                        <v-divider />
                        <v-card-actions class="pa-4" style="gap: 8px;">
                            <v-btn
                                color="primary"
                                variant="flat"
                                rounded="pill"
                                prepend-icon="mdi-content-save"
                                :disabled="!isValid"
                                @click="submitForm"
                            >
                                Enregistrer
                            </v-btn>
                            <v-btn
                                variant="tonal"
                                rounded="pill"
                                prepend-icon="mdi-refresh"
                                @click="resetForm"
                            >
                                Réinitialiser
                            </v-btn>
                            <v-spacer />
                            <v-chip
                                :color="isValid ? 'success' : 'error'"
                                variant="tonal"
                                size="small"
                                :prepend-icon="isValid ? 'mdi-check-circle' : 'mdi-alert-circle'"
                            >
                                {{ isValid ? 'Valide' : `${formErrors.length} erreur(s)` }}
                            </v-chip>
                        </v-card-actions>
                    </v-card>
                </v-col>

                <!-- Right: Raw data + submitted entries -->
                <v-col cols="12" md="6">

                    <!-- Raw JSON preview -->
                    <v-card variant="outlined" rounded="lg" class="mb-4">
                        <v-card-title class="d-flex align-center" style="gap: 8px;">
                            <v-icon icon="mdi-code-json" color="secondary" />
                            Données brutes (temps réel)
                        </v-card-title>
                        <v-card-text class="pa-0">
                            <v-sheet color="surface-variant" rounded="b-lg" class="pa-4">
                                <pre style="font-size: 0.75rem; white-space: pre-wrap; word-break: break-word;">{{
                                    JSON.stringify(formData, null, 2)
                                }}</pre>
                            </v-sheet>
                        </v-card-text>
                    </v-card>

                    <!-- Submitted entries -->
                    <v-card variant="outlined" rounded="lg">
                        <v-card-title class="d-flex align-center" style="gap: 8px;">
                            <v-icon icon="mdi-clipboard-list-outline" color="primary" />
                            Commentaires soumis
                            <v-chip class="ms-2" size="x-small" color="primary" variant="tonal">
                                {{ submittedEntries.length }}
                            </v-chip>
                        </v-card-title>
                        <v-divider />
                        <v-card-text v-if="submittedEntries.length === 0" class="text-center text-medium-emphasis py-6">
                            <v-icon icon="mdi-inbox-outline" size="40" class="mb-2 d-block" />
                            Aucun commentaire soumis pour l'instant.
                        </v-card-text>
                        <v-list v-else lines="three" class="pa-2">
                            <v-list-item
                                v-for="(entry, i) in submittedEntries"
                                :key="i"
                                rounded="lg"
                                class="mb-2"
                                :subtitle="`${entry.equipmentId} — ${entry.date}`"
                                :title="entry.equipmentType ?? '—'"
                            >
                                <template #prepend>
                                    <v-avatar :color="severityColor(entry.severity)" variant="tonal" size="38" rounded="lg">
                                        <v-icon :icon="severityIcon(entry.severity)" size="22" />
                                    </v-avatar>
                                </template>
                                <template #append>
                                    <v-chip
                                        :color="severityColor(entry.severity)"
                                        size="x-small"
                                        variant="tonal"
                                    >
                                        {{ entry.severity }}
                                    </v-chip>
                                </template>
                                <div class="text-body-2 mt-1 text-medium-emphasis" style="white-space: pre-wrap;">
                                    {{ entry.comment }}
                                </div>
                                <div v-if="entry.maintenanceRequired" class="d-flex align-center mt-1" style="gap: 4px;">
                                    <v-icon icon="mdi-wrench" size="14" color="warning" />
                                    <span class="text-caption text-warning">
                                        Maintenance requise
                                        <template v-if="entry.estimatedCost">
                                            — {{ entry.estimatedCost.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' }) }}
                                        </template>
                                    </span>
                                </div>
                            </v-list-item>
                        </v-list>
                    </v-card>

                </v-col>
            </v-row>
        </v-container>

        <!-- Snackbar -->
        <v-snackbar v-model="snackbarVisible" color="success" rounded="pill" :timeout="3000">
            <v-icon icon="mdi-check-circle" class="me-2" />
            {{ snackbarMsg }}
        </v-snackbar>

    </v-card>
</template>
